import { useState, useEffect, useMemo } from 'react';
import { SUBLOCATION_PROGRESSIVE_CHUNK_SIZE, SUBLOCATION_PROGRESSIVE_RENDER_DELAY_MS } from '../constants/pagination';

/**
 * Hook for progressive/chunked rendering of large lists
 * Renders items in batches to keep UI responsive
 * @param {Array} items - Items to render
 * @param {number} chunkSize - How many items per chunk (default: SUBLOCATION_PROGRESSIVE_CHUNK_SIZE)
 * @param {number} delayMs - Delay between chunks in ms (default: SUBLOCATION_PROGRESSIVE_RENDER_DELAY_MS)
 * @returns {Object} { visibleItems, renderedCount, totalCount, isFullyLoaded }
 */
export const useProgressiveRender = (
  items = [],
  chunkSize = SUBLOCATION_PROGRESSIVE_CHUNK_SIZE,
  delayMs = SUBLOCATION_PROGRESSIVE_RENDER_DELAY_MS,
) => {
  const [renderedCount, setRenderedCount] = useState(chunkSize);

  // Reset rendered count when items change
  useEffect(() => {
    setRenderedCount(chunkSize);
  }, [items.length, chunkSize]);

  // Progressive rendering effect
  useEffect(() => {
    if (renderedCount < items.length) {
      const timer = setTimeout(() => {
        setRenderedCount((prev) => Math.min(prev + chunkSize, items.length));
      }, delayMs);
      return () => clearTimeout(timer);
    }
  }, [renderedCount, items.length, chunkSize, delayMs]);

  const visibleItems = useMemo(() => items.slice(0, renderedCount), [items, renderedCount]);
  const isFullyLoaded = renderedCount >= items.length;

  return {
    visibleItems,
    renderedCount,
    totalCount: items.length,
    isFullyLoaded,
  };
};

export default useProgressiveRender;
